import { useState } from "react";
import axios from "axios";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, formatApiError } from "@/contexts/AuthContext";

export default function ClaimAccount() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const email = (params.get("email") || "").trim();
  const token = (params.get("token") || "").trim();
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!email || !token) {
    return (
      <AuthLayout title="Ongeldige activatielink">
        <div className="flex flex-col items-center text-center gap-4 mt-4">
          <XCircle size={40} style={{ color: "#dc2626" }} />
          <p className="text-[14px] text-[#555]">De link is niet compleet. Vraag opnieuw een activatielink aan via support.</p>
          <Link to="/" className="zy-link">Terug naar home</Link>
        </div>
      </AuthLayout>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Gebruik minstens 8 tekens.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/claim-account`, { email, token, password });
      setDone(true);
      toast.success("Wachtwoord ingesteld. Je kunt nu inloggen.");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Activatie mislukt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Stel je wachtwoord in" subtitle={`Account: ${email}`}>
      {done ? (
        <div className="flex flex-col items-center text-center gap-4 mt-4" data-testid="claim-account-success">
          <CheckCircle2 size={40} style={{ color: "#16a34a" }} />
          <p className="text-[14px] text-[#555]">Je wachtwoord is opgeslagen. We sturen je door…</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5 mt-2" data-testid="claim-account-form">
          <div>
            <Label htmlFor="password">Nieuw wachtwoord</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="claim-account-password"
                required
                minLength={8}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#666]"
                data-testid="claim-account-toggle-pw"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-[12px] text-[#666] mt-1.5">Minimaal 8 tekens.</p>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="zy-btn-primary w-full justify-center"
            data-testid="claim-account-submit"
          >
            {submitting ? (<><Loader2 size={14} className="animate-spin" /> Bezig…</>) : "Account activeren"}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
